import { ShineStartDocument } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import useShineStartDocumentMutation from 'features/Onboarding/common/useShineStartDocumentMutation';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';
import DisplayOrUploadDoc from 'features/Onboarding/legacy/components/DisplayOrUploadDoc';
import { UploadDocFromTemplatePageProps } from 'features/Onboarding/legacy/components/UploadDocFromTemplatePage/types';
import { Subtitle } from 'features/Onboarding/legacy/Pages/styles';
import {
  DownloadableNonGenericDocumentLocales,
  NonDownloadableNonGenericDocumentLocales,
} from 'features/Onboarding/shineStartTypes';
import { getUploadShineStartDocumentFunction } from 'features/Storage/upload';

import getDocumentLocales from '../../libs/getDocumentLocales';
import getDownloadFormUrl from '../../libs/getDownloadFormUrl';
import locales from './locales';
import { LinkWrapper, UploadSectionWrapper } from './styles';

function isDownloadableDocumentLocales(
  variableLocales?: UploadDocFromTemplatePageProps['locales'],
): variableLocales is DownloadableNonGenericDocumentLocales {
  if (
    (variableLocales as DownloadableNonGenericDocumentLocales).downloadFormLink
  ) {
    return true;
  }
  return false;
}

function localesHaveUploadDescription(
  variableLocales?: UploadDocFromTemplatePageProps['locales'],
): variableLocales is NonDownloadableNonGenericDocumentLocales {
  if (
    (variableLocales as NonDownloadableNonGenericDocumentLocales)
      .uploadDescription
  ) {
    return true;
  }
  return false;
}

interface DocumentUploadProps {
  onNext: () => void;
  document: ShineStartDocument;
}
const DocumentUpload = asBentoPage<DocumentUploadProps>(
  ({ document, Layout, onNext, onPrev }) => {
    const { shineStartDocumentId, shineStartId } = document;

    const { deleteDocument, isDeletingDocument, updateDocument } =
      useShineStartDocumentMutation({
        shineStartDocumentId,
        shineStartId,
      });

    const variableLocales = getDocumentLocales(document);
    const description =
      'documentDescription' in variableLocales
        ? variableLocales.documentDescription
        : variableLocales.completeDocumentDescription;
    const downloadFormUrl = getDownloadFormUrl(document);
    const uploadFormSubtitle = !isDownloadableDocumentLocales(variableLocales)
      ? locales.uploadFormSubtitle
      : locales.formatString(locales.uploadFormSubtitleWrapper, {
          subtitle: locales.uploadFormSubtitle,
        });
    const existingDocumentUrl =
      document.url && !document.error ? document.url : undefined;
    const isCtaDisabled =
      existingDocumentUrl === undefined || isDeletingDocument;

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Header
          description={description}
          title={variableLocales.documentName}
        />
        {isDownloadableDocumentLocales(variableLocales) ? (
          <>
            <Subtitle>
              {locales.formatString(locales.downloadFormSubtitleWrapper, {
                subtitle: variableLocales.downloadFormSubtitle,
              })}
            </Subtitle>
            <Text>{variableLocales.downloadFormDescription}</Text>
            <LinkWrapper>
              <Button
                as="a"
                href={downloadFormUrl}
                icon="download"
                iconPosition="left"
                target="_blank"
                variant="inline-primary"
              >
                {variableLocales.downloadFormLink}
              </Button>
            </LinkWrapper>
          </>
        ) : null}
        <Subtitle>{uploadFormSubtitle}</Subtitle>
        {localesHaveUploadDescription(variableLocales) ? (
          <Text>{variableLocales.uploadDescription}</Text>
        ) : null}
        <UploadSectionWrapper>
          <DisplayOrUploadDoc
            existingDocumentUrl={existingDocumentUrl}
            isDeletingExistingDocument={isDeletingDocument}
            locales={{
              uploadedDocumentName: variableLocales.uploadedDocumentName,
              uploadFailureMessage: locales.uploadFailureMessage,
              uploadFormBrowse: locales.uploadFormBrowse,
              uploadFormDragDrop: variableLocales.uploadFormDragDrop,
            }}
            onDeleteExistingDocument={() => deleteDocument()}
            onSuccessfulUpload={({ bucketName, fileRef, url }) =>
              updateDocument({
                firebaseBucket: bucketName,
                firebaseRef: fileRef,
                url,
              })
            }
            uploadFile={getUploadShineStartDocumentFunction(shineStartId)}
          />
        </UploadSectionWrapper>

        <OBStickyButton isDisabled={isCtaDisabled} onClick={onNext}>
          {locales.ctaContinue}
        </OBStickyButton>
      </Layout>
    );
  },
);

export default DocumentUpload;
